/**
 * Get Guidelines Tool
 * 返回生成邮件模板时必须遵守的关键规则
 * AI 应该在生成任何模板之前先调用此工具
 */

export interface GetGuidelinesOutput {
  success: boolean;
  guidelines: string;
}

const CRITICAL_GUIDELINES = `
# ⚠️ Easy Email Pro 模板生成关键规则

## 🔴 必须遵守（否则会报错）

### 1. 每个元素必须有 type 和 data
\`\`\`json
{
  "type": "standard-section",  // ✅ 必须
  "data": {},                  // ✅ 必须（可以为空对象）
  "attributes": { ... },
  "children": [ ... ]
}
\`\`\`

### 2. Image 的 width 只支持 px 或留空
\`\`\`json
// ✅ 正确
{ "width": "600px" }
{ "width": "300px" }
{ }  // 留空 = 全宽

// ❌ 错误
{ "width": "100%" }  // 不支持百分比！
\`\`\`

### 3. 所有 standard 元素的 padding 必须显式写出 ⚠️（除 column 外）
\`\`\`json
// ✅ Section
{
  "type": "standard-section",
  "data": {},
  "attributes": {
    "background-color": "#FFFFFF",
    "padding-top": "20px",
    "padding-bottom": "20px",
    "padding-left": "0px",
    "padding-right": "0px"
  }
}

// ✅ Image（使用 Unsplash 占位图，URL 包含宽高参数，支持 fluid-on-mobile）
{
  "type": "standard-image",
  "data": {},
  "attributes": {
    "src": "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=300&h=300&fit=crop",
    "width": "300px",
    "padding-top": "0px",
    "padding-left": "0px",
    "padding-right": "0px",
    "padding-bottom": "0px",
    "fluid-on-mobile": "true"
  },
  "children": [{ "text": "" }]
}

// ✅ Paragraph（所有文本元素也需要 padding）
{
  "type": "standard-paragraph",
  "data": {},
  "attributes": {
    "align": "left",
    "color": "#333333",
    "padding-top": "0px",
    "padding-bottom": "10px",
    "padding-left": "0px",
    "padding-right": "0px"
  },
  "children": [{ "text": "Content" }]
}

// ✅ Button（需要 padding、inner-padding 和 line-height）
{
  "type": "standard-button",
  "data": {
    "content": "Button"
  },
  "attributes": {
    "align": "left",
    "line-height": "130%",
    "padding-top": "30px",
    "padding-left": "20px",
    "padding-right": "20px",
    "background-color": "#FC8C26",
    "inner-padding-top": "10px"
  },
  "children": [{ "text": "Learn More" }]
}

// ✅ Column（不需要 padding，这是唯一例外）
{
  "type": "standard-column",
  "data": {},
  "attributes": {
    "width": "50%",
    "vertical-align": "top"
    // 注意：Column 不需要 padding 属性
  },
  "children": [...]
}
\`\`\`

### 4. 背景色和文字颜色必须匹配 ⚠️ 关键！

**深色背景（红、黑、深蓝等）** → 文字必须是**白色或浅色**
**浅色背景（白、浅灰等）** → 文字必须是**黑色或深色**

❌ **常见错误**：红色背景上使用黑色文字（看不见！）
✅ **正确做法**：红色背景上使用白色文字

\`\`\`json
// ✅ 红色背景 + 白色文字
{
  "type": "standard-section",
  "data": {},
  "attributes": {
    "background-color": "#FF0000",  // 红色背景
    "padding-top": "40px",
    "padding-bottom": "40px",
    "padding-left": "20px",
    "padding-right": "20px"
  },
  "children": [
    {
      "type": "standard-column",
      "data": {},
      "attributes": { "width": "100%" },
      "children": [
        {
          "type": "standard-h1",
          "data": {},
          "attributes": {
            "color": "#FFFFFF",  // ⚠️ 白色文字！
            "align": "left"
          },
          "children": [{ "text": "CYBER MONDAY DEALS" }]
        }
      ]
    }
  ]
}
\`\`\`

### 5. 非文本元素的 children 必须是 [{ text: "" }]
\`\`\`json
// ✅ Image, Divider, Spacer, Social-Element
"children": [{ "text": "" }]

// ❌ 错误
"children": []
\`\`\`

### 6. 文本元素的 children 是 TextNode 数组
\`\`\`json
// ✅ Paragraph, H1-H4, Button
"children": [
  { "text": "普通文本" },
  { "text": "加粗", "bold": true },
  { "text": "链接", "link": { "href": "https://..." }, "underline": true }
]
\`\`\`

### 7. 文本对齐方式必须准确识别 ⚠️
\`\`\`json
// ✅ 根据实际视觉判断对齐方式
{
  "type": "standard-h2",
  "attributes": {
    "align": "center"  // ✅ Hero 区块标题通常居中
  }
}

{
  "type": "standard-paragraph",
  "attributes": {
    "align": "left"  // ✅ 正文段落通常左对齐
  }
}

{
  "type": "standard-button",
  "attributes": {
    "align": "center"  // ✅ 按钮通常居中
  }
}
\`\`\`

**判断方法**：
- **center（居中）**：文本左右两侧都有大致相等的空白
- **left（左对齐）**：文本从容器最左侧开始，右边缘不整齐
- **right（右对齐）**：文本在容器最右侧结束，左边缘不整齐

⚠️ **不要假设所有文本都是相同的对齐方式！每个元素都要单独判断！**

---

## 📋 结构层级

\`\`\`
page
└── sections[]
    ├── standard-section (独立的内容块)
    │   └── children[] (Column 数组)
    │       └── standard-column
    │           └── children[] (Element 数组)
    │               └── standard-xxx (paragraph/image/button...)
    │                   └── children[] (TextNode 数组)
    │
    └── standard-wrapper (容器，包裹多个相关的 Section)
        └── children[] (Section 数组)
            └── standard-section
                └── children[] (Column 数组)
                    └── ...
\`\`\`

⚠️ **重要区别**：
- **Section** 的 children 是 **Column 数组**
- **Wrapper** 的 children 是 **Section 数组**

---

## 🎨 常用元素类型

| 类型 | 用途 | children | 说明 |
|------|------|----------|------|
| standard-wrapper | 容器 | Section[] | 包裹多个相关的 Section，设置统一背景色/padding |
| standard-section | 行容器 | Column[] | 独立的内容块，**可以设置 background-color** |
| standard-column | 列容器 | Element[] | **不需要 padding**（唯一例外） |
| standard-paragraph | 正文 | TextNode[] | padding 可以只写需要的部分，支持 underline 等文本样式，**注意文字颜色与背景色匹配** |
| standard-h1 ~ h4 | 标题 | TextNode[] | padding 可以只写需要的部分，**注意文字颜色与背景色匹配** |
| standard-image | 图片 | [{ text: "" }] | padding 必须显式写出 |
| standard-button | 按钮 | TextNode[] | padding 必须显式写出，**注意按钮背景色和文字颜色匹配** |
| standard-divider | 分割线 | [{ text: "" }] | padding 必须显式写出 |
| standard-spacer | 间距 | [{ text: "" }] | padding 必须显式写出 |
| standard-navbar | 导航栏 | NavbarLink[] | padding 必须显式写出 |
| standard-social | 社交图标 | SocialElement[] | padding 必须显式写出，使用 spacing 和 icon-size |
| standard-social-element | 单个社交图标 | [] | 使用 src 属性指向图标图片 URL，或使用 name 属性（facebook/twitter/instagram等） |

---

## 🖼️ 图片规则

### 默认使用 Unsplash 图片（支持宽高参数）
所有图片 src 必须使用 Unsplash URL，**并在 URL 中添加宽高参数**以确保尺寸匹配：

\`https://images.unsplash.com/photo-xxx?w=600&h=400&fit=crop\`

**URL 参数说明**：
- \`w\`: 宽度（像素）
- \`h\`: 高度（像素）
- \`fit=crop\`: **裁剪图片以完全填充指定尺寸**（推荐，确保尺寸精确匹配）

### Alt 描述要求（用于后续 AI 图片生成）⚠️ 必须包含宽高信息！

**格式**：\`"图片内容详细描述 [Width: XXXpx, Height: XXXpx]"\`

**必须包含**：
1. 图片内容的详细描述（场景、对象、风格、光线等）
2. **图片的宽高信息**（格式：\`[Width: XXXpx, Height: XXXpx]\`）

✅ 好的描述（包含宽高）：
- "Professional business team in modern office, 4 diverse people collaborating around white table, natural lighting [Width: 600px, Height: 400px]"
  - URL: \`https://images.unsplash.com/photo-xxx?w=600&h=400&fit=crop\`
- "Elegant product photography of skincare bottles on white marble, soft shadows, luxury branding [Width: 300px, Height: 300px]"
  - URL: \`https://images.unsplash.com/photo-xxx?w=300&h=300&fit=crop\`
- "Delicious gourmet cheeseburger with melted cheese, red onion, lettuce, and french fries on rustic wooden board [Width: 600px, Height: 300px]"
  - URL: \`https://images.unsplash.com/photo-xxx?w=600&h=300&fit=crop\`

❌ 差的描述（缺少宽高或描述不够详细）：
- "Team photo" ❌
- "Product image" ❌
- "Delicious burger" ❌（缺少宽高信息）

⚠️ **宽高信息必须准确**，否则后续 AI 生成的图片尺寸不匹配会导致对齐问题！

### 常用 Unsplash 图片示例
- 工作场景: https://images.unsplash.com/photo-1517694712202-14dd9538aa97
- 团队协作: https://images.unsplash.com/photo-1522071820081-009f0129c71c
- 产品展示: https://images.unsplash.com/photo-1523275335684-37898b6baf30
- 美食摄影: https://images.unsplash.com/photo-1504674900247-0877df9cc836
- 城市风光: https://images.unsplash.com/photo-1449824913935-59a10b8d2000

---

## 📦 Wrapper（容器）使用规则

### 何时使用 Wrapper

⚠️ **当多个 Section 在视觉上属于同一个容器/分组时，应该使用 Wrapper 包裹它们！**

**使用场景**：
- 多个相关的 Section 在视觉上属于同一个容器
- 例如：两个内容块（顶部和底部）属于同一个视觉分组
- 需要为多个 Section 设置统一的背景色、padding 等样式

**不使用场景**：
- 每个 Section 都是独立的，没有明显的容器关系
- 通常按行（Section）逐个生成即可

### Wrapper 结构

\`\`\`json
{
  "type": "standard-wrapper",
  "data": {},
  "attributes": {
    "background-color": "#F5F5F5",
    "padding-top": "20px",
    "padding-bottom": "20px",
    "padding-left": "0px",
    "padding-right": "0px"
  },
  "children": [
    // ⚠️ Wrapper 的 children 是 Section 数组（不是 Column 数组）
    {
      "type": "standard-section",
      "data": {},
      "attributes": { ... },
      "children": [ /* Column 数组 */ ]
    },
    {
      "type": "standard-section",
      "data": {},
      "attributes": { ... },
      "children": [ /* Column 数组 */ ]
    }
  ]
}
\`\`\`

⚠️ **重要**：
- **Section** 的 children 是 **Column 数组**
- **Wrapper** 的 children 是 **Section 数组**
- Wrapper 也需要显式写出 padding（除 column 外，所有 standard 元素都需要 padding）

---

## 📐 布局描述方法论

### 观察顺序

**按照以下顺序系统性地观察和描述邮件模板**：

1. **整体观察**：从上到下扫描整个邮件，了解整体结构
2. **区块识别**：识别每个独立的视觉区块（Section），注意背景色变化和内容分组
3. **布局分析**：分析每个区块内的布局结构（单列/多列、列宽比例）
4. **元素识别**：识别每个元素类型和特征（图片、文本、按钮等）

### 描述层次

**按照以下层次结构进行描述**：

- **Level 1: Page（页面层级）**
  - 整体背景色（\`background-color\`）
  - 内容背景色（\`content-background-color\`）
  - 页面宽度（通常 600px）

- **Level 2: Section（区块层级）**
  - 区块背景色（是否有统一的背景色）
  - Padding 和间距（上下左右的内边距）
  - 内容分组（是否属于同一个视觉容器，判断是否需要 Wrapper）

- **Level 3: Column（列层级）**
  - 列数（单列、两列、三列等）
  - 列宽比例（如 50%-50%、40%-60%、33%-67%）
  - 列内内容分布

- **Level 4: Element（元素层级）**
  - 元素类型（图片、文本、按钮等）
  - 元素属性（颜色、对齐、大小等）
  - 元素内容（文本内容、图片描述等）

---

## 📋 如何描述 Section（区块）

### Section 识别方法

**识别 Section 的关键特征**：

1. **视觉边界**：观察是否有明显的视觉分隔（背景色变化、间距变化）
2. **背景色**：是否有统一的背景色（与上下 Section 不同）
3. **内容分组**：内容是否在视觉上属于同一个逻辑分组
4. **Padding 表现**：观察上下左右的空白间距

### Section 描述要点

**描述 Section 时应包括以下信息**：

1. **位置**：第几个 Section（从上到下编号）
2. **背景**：背景色或背景图（如 "红色背景 #FF0000"、"白色背景 #FFFFFF"）
3. **布局**：单列/多列，列宽比例（如 "两列布局，40%-60%"）
4. **内容**：包含哪些元素（图片、文本、按钮等）
5. **间距**：padding 的视觉表现（如 "上下 padding 40px，左右 padding 20px"）
6. **容器关系**：是否与其他 Section 属于同一个视觉容器（判断是否需要 Wrapper）

### Section 描述示例

**示例 1：单列 Section（Logo 和品牌名称）**
- "第 2 个 Section：白色背景，单列布局，包含 Logo 图片（居中）和 'Easy Email Pro' 文本（居中），上下 padding 15px，左右 padding 20px"

**示例 2：多列 Section**
- "第 3 个 Section：红色背景 #FF0000，两列布局（40%-60%），左侧是大标题 'CYBER MONDAY DEALS'，右侧是段落文字和按钮，上下 padding 40px，左右 padding 20px"

**示例 3：需要 Wrapper 的情况**
- "第 2-4 个 Section：这三个 Section 在视觉上属于同一个灰色背景容器，应该使用 Wrapper 包裹，Wrapper 背景色 #F5F5F5"

---

## 📐 如何描述 Layout（布局）

### 单列布局

**识别特征**：
- 内容垂直排列，没有左右分栏
- 所有内容占据 100% 宽度

**描述方法**：
- "单列布局，Column width: 100%"
- "内容垂直排列，包含：图片、标题、段落"

### 多列布局

**识别特征**：
- 内容左右分栏排列
- 需要观察列宽比例

**描述方法**：
- **两列布局**："两列布局，左侧 50%，右侧 50%"
- **不等宽两列**："两列布局，左侧 40%，右侧 60%"
- **三列布局**："三列布局，每列 33.33%"

**列宽比例判断技巧**：
1. 观察视觉比例：如果左侧内容明显更窄 → 可能是 40%-60% 或 33%-67%
2. 观察内容量：如果左侧内容少、右侧内容多 → 可能是 40%-60%
3. 观察对齐方式：如果左右内容对齐方式不同 → 可能是 50%-50%

### 混合布局

**识别特征**：
- 同一个 Section 内可能有多种布局方式
- 某些元素可能跨列

**描述方法**：
- "混合布局：第一行单列（图片），第二行两列（文字+图片）"
- "复杂布局：顶部单列标题，中间两列内容，底部单列按钮"

### 布局描述模板

**标准描述格式**：
\`\`\`
布局类型：[单列/两列/三列/混合]
列宽比例：[如 50%-50%、40%-60%]
内容分布：
  - 左列：[元素列表]
  - 右列：[元素列表]
对齐方式：[左对齐/居中/右对齐] ⚠️ 必须准确识别每个元素的对齐方式
\`\`\`

**对齐方式识别要点**：
- 观察每个元素（图片、文本）在容器中的位置
- **Logo 和品牌名称通常在同一列中，使用相同的对齐方式（通常是 center）**
- 产品网格中的图片和文字通常都居中
- Header 中的 Logo 和品牌名称通常都居中

**示例**：
\`\`\`
布局类型：两列
列宽比例：40%-60%
内容分布：
  - 左列：H1 标题 "CYBER MONDAY DEALS"
  - 右列：段落文字 "There's still time to save!" + 按钮 "Browse Deals"
对齐方式：左对齐
\`\`\`

---

## 📝 如何描述文本元素

### 文本层级识别

**识别不同类型的文本元素**：

1. **标题（Heading）**
   - H1：最大标题，通常用于主标题
   - H2：二级标题，用于章节标题
   - H3：三级标题，用于小节标题
   - H4：四级标题，用于小标题

2. **段落（Paragraph）**
   - 正文段落，通常用于描述性文字

3. **链接（Link）**
   - 有下划线的文本，没有背景色
   - 在段落中使用 \`underline: true\`

4. **按钮（Button）**
   - 有背景色的可点击元素
   - 使用 \`standard-button\` 元素

### 文本特征描述

**描述文本时应包括以下信息**：

1. **类型**：标题（H1/H2/H3/H4）、段落、链接、按钮
2. **内容**：具体文字内容（完整文本，包括换行）
3. **样式**：
   - 字体大小（如 "48px"、"16px"、"14px"）
   - 颜色（如 "#FFFFFF"、"#333333"）
   - 对齐方式（左对齐/居中/右对齐）
   - 字重（加粗/正常）
   - 其他样式（下划线、斜体等）
4. **位置**：在哪个 Section、哪个 Column

**对齐方式识别要点**：
- 如果文本在容器中左右对称 → **center**
- 如果文本从容器左侧开始 → **left**
- 如果文本在容器右侧结束 → **right**
- **Logo 和品牌名称通常在同一列中，使用相同的对齐方式（通常是 center）**

### 多行文本描述

**如何描述多行文本**：

1. **识别换行**：观察文本是否在视觉上分为多行
2. **描述换行位置**：明确标注每行的内容
3. **描述换行方式**：使用 \`\\n\` 或单独的 TextNode

**示例**：
- "多行标题：'CYBER'（第一行）、'MONDAY'（第二行）、'DEALS'（第三行）"
- "段落文本：'There's still time to save!'（第一行）、'Up to 65% off while supplies last.'（第二行）"

### 文本描述模板

**标准描述格式**：
\`\`\`
文本类型：[H1/H2/H3/H4/Paragraph/Link/Button]
内容：[完整文本内容]
样式：
  - 字体大小：[如 48px]
  - 颜色：[如 #FFFFFF]
  - 对齐：[左/中/右]
  - 字重：[加粗/正常]
  - 其他：[下划线/斜体等]
位置：[Section X, Column Y]
\`\`\`

**示例**：
\`\`\`
文本类型：H1
内容：CYBER\\nMONDAY\\nDEALS
样式：
  - 字体大小：48px
  - 颜色：#FFFFFF
  - 对齐：左对齐
  - 字重：加粗
位置：Section 3, Column 1（左侧）
\`\`\`

---

## 🎨 视觉细节识别指南（关键！）

### 1. 背景色识别 ⚠️ 必须准确识别！

**深色背景上的文字必须使用浅色（白色/浅灰）**：
- 红色背景 → 白色文字
- 黑色背景 → 白色文字
- 深色背景 → 白色文字

**浅色背景上的文字使用深色**：
- 白色背景 → 黑色/深灰文字
- 浅色背景 → 黑色文字

\`\`\`json
// ✅ 红色背景 + 白色文字
{
  "type": "standard-section",
  "data": {},
  "attributes": {
    "background-color": "#FF0000",  // 红色背景
    "padding-top": "40px",
    "padding-bottom": "40px",
    "padding-left": "20px",
    "padding-right": "20px"
  },
  "children": [
    {
      "type": "standard-column",
      "data": {},
      "attributes": { "width": "100%" },
      "children": [
        {
          "type": "standard-h1",
          "data": {},
          "attributes": {
            "color": "#FFFFFF",  // ⚠️ 白色文字！
            "align": "left"
          },
          "children": [{ "text": "CYBER MONDAY DEALS" }]
        }
      ]
    }
  ]
}
\`\`\`

### 2. 多行标题识别

**如果标题在视觉上分为多行，使用多个 TextNode 或换行符**：

\`\`\`json
// 方式 1：使用多个 TextNode（推荐）
{
  "type": "standard-h1",
  "data": {},
  "attributes": { "color": "#FFFFFF" },
  "children": [
    { "text": "CYBER" },
    { "text": "\\n" },  // 换行
    { "text": "MONDAY" },
    { "text": "\\n" },
    { "text": "DEALS" }
  ]
}

// 方式 2：使用单个 TextNode 包含换行符
{
  "type": "standard-h1",
  "data": {},
  "attributes": { "color": "#FFFFFF" },
  "children": [
    { "text": "CYBER\\nMONDAY\\nDEALS" }
  ]
}
\`\`\`

### 3. 左右分栏布局识别

**识别左右分栏时，注意列宽比例**：
- 文字在左，图片在右 → 通常 50%-50% 或 40%-60%
- 文字在左，图片在右（文字较少） → 可能 33%-67% 或 40%-60%

\`\`\`json
{
  "type": "standard-section",
  "data": {},
  "attributes": {
    "padding-top": "20px",
    "padding-bottom": "20px",
    "padding-left": "20px",
    "padding-right": "20px"
  },
  "children": [
    {
      "type": "standard-column",
      "data": {},
      "attributes": { "width": "50%" },  // 左侧文字
      "children": [
        { "type": "standard-h3", ... },
        { "type": "standard-button", ... }
      ]
    },
    {
      "type": "standard-column",
      "data": {},
      "attributes": { "width": "50%" },  // 右侧图片
      "children": [
        { "type": "standard-image", ... }
      ]
    }
  ]
}
\`\`\`

### 4. 按钮中的特殊字符

**按钮文本中的箭头、符号等必须保留**：

\`\`\`json
{
  "type": "standard-button",
  "data": { "content": "Button" },
  "attributes": {
    "background-color": "#FF0000",
    "color": "#FFFFFF"
  },
  "children": [
    { "text": "See More Savings →" }  // ⚠️ 箭头符号必须保留
  ]
}
\`\`\`

### 5. 按钮 vs 链接识别 ⚠️ 关键区别！

**必须准确区分按钮和链接**：

#### 按钮的特征：
- **有背景色**（如橙色、红色、蓝色等）
- **通常是矩形/圆角矩形**
- **文字在背景色上**（通常是白色文字）
- **使用 \`standard-button\` 元素**

#### 链接的特征：
- **没有背景色**（透明背景）
- **只有下划线**（\`underline: true\`）
- **文字颜色可能与段落相同或不同**
- **在 \`standard-paragraph\` 中使用 \`underline\` 或 \`link\` 属性**

⚠️ **常见错误**：将按钮误识别为链接文本！

**判断方法**：
1. 观察是否有明显的背景色块 → 是按钮
2. 观察是否只有下划线，没有背景色 → 是链接
3. 观察文字是否在彩色背景上 → 是按钮
4. 观察文字是否在透明背景上，只有下划线 → 是链接

**示例场景**：
- 红色背景区块中，右侧有橙色背景的 "Browse Deals" → **这是按钮**，不是链接！
- 白色背景段落中，有下划线的 "unsubscribe" → **这是链接**，不是按钮！

### 6. 段落和按钮的分离 ⚠️ 注意结构！

**段落文字和按钮应该分开，不要混在一起**：

⚠️ **常见错误**：将按钮文本放在段落中作为链接！

**正确做法**：
- 段落文字使用 \`standard-paragraph\`
- 按钮使用独立的 \`standard-button\` 元素
- 它们可以是同一列中的不同元素，但不要混在一起

**示例**：
- ❌ 错误：在段落中写 "Browse Deals" 并设置 \`underline: true\`（这是链接，不是按钮）
- ✅ 正确：段落写文字，按钮单独写，按钮有背景色

### 6. 段落和按钮的分离 ⚠️ 注意结构！

**段落文字和按钮应该分开，不要混在一起**：

⚠️ **常见错误**：将按钮文本放在段落中作为链接！

**正确做法**：
- 段落文字使用 \`standard-paragraph\`
- 按钮使用独立的 \`standard-button\` 元素
- 它们可以是同一列中的不同元素，但不要混在一起

**示例**：
- ❌ 错误：在段落中写 "Browse Deals" 并设置 \`underline: true\`（这是链接，不是按钮）
- ✅ 正确：段落写文字，按钮单独写，按钮有背景色

### 7. 全宽图片 vs 有边距的图片

**识别图片是否有左右边距**：
- 全宽图片：section padding-left/right = "0px"
- 有边距图片：section padding-left/right = "20px" 或其他值

### 8. 对齐方式识别 ⚠️ 必须准确识别！

**对齐方式影响视觉效果，必须仔细观察每个元素在容器中的位置！**

#### 图片对齐（Image align）

**识别方法**：
- **center（居中）**：图片在容器中居中，左右两侧空白大致相等
- **left（左对齐）**：图片紧贴容器左侧
- **right（右对齐）**：图片紧贴容器右侧

**常见场景**：
- **Logo 在 Header 中** → 通常 **center**（居中）⚠️ 重要！
- **品牌名称在 Logo 下方** → 通常 **center**（居中）⚠️ 重要！
- 产品图片在网格中 → 通常 **center**（居中）
- 全宽 Hero 图片 → 通常 **center**（居中）
- 左右分栏中的图片 → 根据设计可能是 **left** 或 **center**

⚠️ **Logo 和品牌名称通常在同一列中，使用相同的对齐方式（通常是 center）！**

#### 文本对齐（Text align）

**识别方法**：
- **center（居中）**：文本在容器中居中，左右两侧空白大致相等
- **left（左对齐）**：文本从容器最左侧开始，右边缘不整齐
- **right（右对齐）**：文本在容器最右侧结束，左边缘不整齐

**常见场景**：
- Header Logo 下方的品牌名称 → 通常 **center**（居中）
- Hero 区块的标题 → 通常 **center**（居中）
- 正文段落 → 通常 **left**（左对齐）或 **center**（居中）
- 按钮 → 通常 **center**（居中）
- 在深色背景上，白色文字通常左对齐或居中

#### 对齐方式判断技巧

1. **观察元素与容器的关系**：
   - 如果元素在容器中左右对称 → **center**
   - 如果元素紧贴容器左侧 → **left**
   - 如果元素紧贴容器右侧 → **right**

2. **对比同一列中的多个元素**：
   - **如果 Logo 和文本在同一列** → 它们通常使用相同的对齐方式（通常是 center）⚠️ 重要！
   - 如果同一列中的多个元素都对齐 → 观察它们的对齐方式

3. **参考常见模式**：
   - **Header 中的 Logo 和品牌名称** → 通常都居中 ⚠️ 重要！
   - 产品网格中的图片和文字 → 通常都居中
   - 左右分栏中的内容 → 通常左对齐

⚠️ **重要**：不要假设！每个元素都要单独观察和判断！特别是 Logo 和品牌名称，通常都是居中对齐！

---

## 📝 推荐工作流

### 阶段 1：观察和描述（关键！）

**在生成 JSON 之前，必须先进行系统性的观察和描述**：

1. **整体观察**：从上到下扫描整个邮件，了解整体结构
2. **Section 识别**：识别每个独立的视觉区块（Section），描述其位置、背景、布局、内容、间距
3. **Layout 分析**：分析每个 Section 的布局结构（单列/多列、列宽比例、内容分布）
4. **文本描述**：描述每个文本元素的类型、内容、样式、位置
5. **视觉细节**：识别背景色、文字颜色、对齐方式、按钮 vs 链接等关键细节

### 阶段 2：获取默认属性

6. **调用 get_guidelines**（本工具）了解关键规则
7. **调用 get_element_schema({ elementType: "standard-xxx" })** 获取元素的完整默认属性

### 阶段 3：生成 JSON

8. **基于描述和默认属性**编写 Section JSON，只修改需要自定义的值
9. **逐个调用 generate_section** 生成每个区块
10. **调用 assemble_page** 组装完整模板
11. **调用 convert_to_mjml → convert_to_html** 获取最终 HTML

### 阶段 4：验证和修正

12. **生成图片对比**：将生成的 JSON 转换为图片，与原图对比
13. **识别差异**：找出布局、颜色、对齐、间距等方面的差异
14. **修正 JSON**：根据差异修正 JSON，重新生成图片
15. **迭代优化**：重复步骤 12-14，直到生成的图片与原图基本一致

### 获取默认属性示例

\`\`\`
// 查询按钮的完整默认属性
get_element_schema({ elementType: "standard-button" })

// 返回：
{
  "allDefaultAttributes": {
    "align": "center",
    "background-color": "#414141",
    "color": "#ffffff",
    "font-family": "Arial",
    "font-size": "13px",
    "border-radius": "3px",
    "padding-top": "10px",
    "padding-bottom": "10px",
    ...
  }
}
\`\`\`

### 常用元素类型查询

- \`get_element_schema({ elementType: "standard-section" })\`
- \`get_element_schema({ elementType: "standard-column" })\`
- \`get_element_schema({ elementType: "standard-paragraph" })\`
- \`get_element_schema({ elementType: "standard-image" })\`
- \`get_element_schema({ elementType: "standard-button" })\`
- \`get_element_schema({ elementType: "standard-h1" })\`
- \`get_element_schema({ elementType: "standard-divider" })\`
- \`get_element_schema({ elementType: "standard-social" })\`
- \`get_element_schema({})\` → 返回所有支持的元素类型列表
`;

export function getGuidelines(): GetGuidelinesOutput {
  return {
    success: true,
    guidelines: CRITICAL_GUIDELINES,
  };
}

export const GET_GUIDELINES_TOOL = {
  name: "get_guidelines",
  description: `【重要】获取生成邮件模板的关键规则和注意事项。

⚠️ 请在生成任何模板之前先调用此工具！

返回内容包括：
- 必须遵守的规则（type/data 字段、image width、padding 等）
- 结构层级说明
- 常用元素类型
- 图片 alt 描述要求
- 推荐工作流

调用方式：get_guidelines({})`,
  inputSchema: {
    type: "object" as const,
    properties: {},
    required: [],
  },
};
